import type { ReactNode } from 'react';
import type {
  AdminProductPriceSummary,
  MissingPriceFilter,
  ReleaseChannelKey,
  SalesPlatform,
} from '@/api/admin';
import { formatPriceInput } from '@/lib/price-input';
import { cn } from '@/lib/utils';

export const PLATFORM_LABEL: Record<SalesPlatform, string> = {
  DIRECT_SALE: 'Direct Sale',
  SELLER_CREATOR: 'Seller Creator',
};

interface PriceSummaryStripProps {
  summary: AdminProductPriceSummary | undefined;
  /** Shows placeholders until the first counts arrive. */
  isLoading: boolean;
  platform: SalesPlatform;
  /** The missing-price filter the list currently applies, if any. */
  missing: MissingPriceFilter | undefined;
  /** Called with the next filter; `undefined` clears it. */
  onMissingChange: (next: MissingPriceFilter | undefined) => void;
  /** The status filter the list currently applies, if any. */
  status: boolean | undefined;
  /** Called with the next status filter; `undefined` clears it. */
  onStatusChange: (next: boolean | undefined) => void;
  /** The channel filter the list currently applies, if any. */
  channel: ReleaseChannelKey | undefined;
  /** Release state of the channel filter; undefined counts as true. */
  released: boolean | undefined;
  /**
   * Called with the channel whose released products to show (sets
   * `released=true`); `undefined` clears channel and released.
   */
  onChannelChange: (next: ReleaseChannelKey | undefined) => void;
}

const CARD_CLASS = 'rounded-card bg-paper-2 px-4 py-3 ring-1 ring-line';

/**
 * One header number. With `onClick` it is a toggle button that applies
 * the matching list filter; `selected` marks the card whose filter is on.
 */
function Stat({
  label,
  value,
  detail,
  tone = 'default',
  loading,
  selected = false,
  onClick,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'default' | 'warning';
  loading: boolean;
  selected?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <p className="dc-eyebrow">{label}</p>
      {loading ? (
        <>
          <span
            className="mt-1 block h-6 w-16 animate-pulse rounded-chip bg-paper-3"
            aria-hidden="true"
          />
          <span className="sr-only">Loading</span>
        </>
      ) : (
        <p
          className={cn(
            'mt-1 font-mono text-lg text-ink',
            tone === 'warning' && 'text-warning'
          )}
        >
          {value}
        </p>
      )}
      {detail && !loading && <p className="text-xs text-mute">{detail}</p>}
    </>
  );

  if (!onClick) return <div className={CARD_CLASS}>{body}</div>;

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      title={selected ? 'Click to show all products' : 'Click to filter'}
      className={cn(
        CARD_CLASS,
        'block w-full cursor-pointer text-left transition-shadow',
        'hover:ring-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        selected && 'ring-2 ring-primary'
      )}
    >
      {body}
    </button>
  );
}

/**
 * Header numbers of the price table (R12), counted by the API over every
 * product matching the filters except the card's own one, so the counts
 * stay put while a card is used as a filter. Missing counts turn orange
 * once they are above zero; the base prices name the chosen platform.
 *
 * The count cards double as filters: the active and inactive cards set
 * the status filter, the missing cards the matching missing-price
 * filter, the channel cards the released products of their channel
 * (`channel=<KEY>&released=true`). A second click clears the filter again.
 */
export function PriceSummaryStrip({
  summary,
  isLoading,
  platform,
  missing,
  onMissingChange,
  status,
  onStatusChange,
  channel,
  released,
  onChannelChange,
}: PriceSummaryStripProps) {
  const loading = isLoading || !summary;
  const warn = (count: number | null | undefined) =>
    count && count > 0 ? 'warning' : 'default';
  const toggleMissing = (filter: MissingPriceFilter) => () =>
    onMissingChange(missing === filter ? undefined : filter);
  const toggleStatus = (next: boolean) => () =>
    onStatusChange(status === next ? undefined : next);
  const channelSelected = (key: ReleaseChannelKey) =>
    channel === key && released !== false;
  const toggleChannel = (key: ReleaseChannelKey) => () =>
    onChannelChange(channelSelected(key) ? undefined : key);

  return (
    <section
      aria-label="Price summary"
      className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6"
    >
      <Stat
        label="Active"
        loading={loading}
        value={summary?.active ?? 0}
        detail="products"
        selected={status === true}
        onClick={toggleStatus(true)}
      />
      <Stat
        label="Inactive"
        loading={loading}
        value={summary?.inactive ?? 0}
        detail="products"
        selected={status === false}
        onClick={toggleStatus(false)}
      />
      {(summary?.releaseChannels ?? []).map((releaseChannel) => (
        <Stat
          key={releaseChannel.key}
          label={releaseChannel.name}
          loading={loading}
          value={summary?.channels[releaseChannel.key] ?? 0}
          detail={`released (${releaseChannel.key})`}
          selected={channelSelected(releaseChannel.key)}
          onClick={toggleChannel(releaseChannel.key)}
        />
      ))}
      <Stat
        label="Without blank cost"
        loading={loading}
        value={summary?.missingPurchasePrice ?? 0}
        tone={warn(summary?.missingPurchasePrice)}
        selected={missing === 'purchasePrice'}
        onClick={toggleMissing('purchasePrice')}
      />
      <Stat
        label="Without sales price"
        loading={loading}
        value={summary?.missingRecommendedSalesPrice ?? 0}
        detail="placeholders 999.99 count as missing"
        tone={warn(summary?.missingRecommendedSalesPrice)}
        selected={missing === 'recommendedSalesPrice'}
        onClick={toggleMissing('recommendedSalesPrice')}
      />
      <Stat
        label="Without decoration price"
        loading={loading}
        value={summary?.missingDecorationPrice ?? '–'}
        detail={PLATFORM_LABEL[platform]}
        tone={warn(summary?.missingDecorationPrice)}
        selected={missing === 'decorationPrice'}
        onClick={toggleMissing('decorationPrice')}
      />
      <div className={CARD_CLASS}>
        <p className="dc-eyebrow">Base prices · {PLATFORM_LABEL[platform]}</p>
        {loading ? (
          <span
            className="mt-1 block h-6 w-32 animate-pulse rounded-chip bg-paper-3"
            aria-hidden="true"
          />
        ) : summary.basePrices.length === 0 ? (
          <p className="mt-1 text-sm text-mute">No methods</p>
        ) : (
          <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm">
            {summary.basePrices.map((base) => (
              <li key={base.methodId} className="whitespace-nowrap">
                <span className="text-ink-2">{base.code}</span>{' '}
                <span
                  className={cn(
                    'font-mono',
                    base.price === null ? 'text-warning' : 'text-ink'
                  )}
                >
                  {base.price === null
                    ? 'no price'
                    : formatPriceInput(base.price)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
